import type { GetCheckoutLinksListItem } from "@birdwatch/client";

import {
  isPresaleCheckoutLinkKey,
  type PresaleCampaign,
} from "@/features/shared/presale";

/** Product keys the presale cart is built from, as seeded in the `Product` catalog. */
const MEMBERSHIP_PRODUCT_TYPE = "PLAN";
/** `Product.key` of the prepaid balance the campaign codes cover. */
export const SERVICE_CREDIT_KEY = "service-credit";

/** One priced line of a founding offer, flattened out of the checkout link. */
export type PresaleCartLine = {
  /** `Product.key` — what the cart groups and labels by. */
  key: string;
  /** `Product.id` — what a checkout link's line item is keyed by, and what offers match on. */
  productId: string;
  /** The Stripe price the link pins for this line. `null` when it uses the product default. */
  priceId: string | null;
  name: string;
  description: string;
  quantity: number;
  /** Unit price in cents, as Stripe holds it. `null` when the price failed to resolve. */
  unitAmountCents: number | null;
  /** `"month"` for the membership; `null` for the one-time services. */
  interval: string | null;
  /** Stripe `prod_…` — what a coupon's `applies_to.products` names. `null` when unresolved. */
  stripeProductId: string | null;
  isMembership: boolean;
};

/**
 * A founding offer — one `CheckoutLink` row of the campaign, with its cart resolved.
 *
 * The four Loudon links differ only in their service-credit price, so `creditCents`
 * is what the picker shows and everything else is identical between them.
 */
export type PresaleOffer = {
  /** `CheckoutLink.id` — what `add-my-place` and the purchase-intent update take. */
  checkoutLinkId: string;
  key: string;
  name: string;
  description: string;
  lines: PresaleCartLine[];
  /** The service credit this offer includes, in cents. `0` when it sells none. */
  creditCents: number;
};

/** The fields of a checkout-link line item this module needs, from `list` or the detail. */
type CheckoutLinkLine = {
  productId: string;
  usedPriceId?: string | null;
  product?: { key?: string | null } | null;
  usedPriceDetail?: { unitAmount?: number | null } | null;
};

/** The service credit a checkout link sells — the one line that identifies its tier. */
export type PresaleOfferTarget = {
  productId: string;
  priceId: string | null;
  unitAmountCents: number | null;
};

function toCartLine(
  lineItem: NonNullable<GetCheckoutLinksListItem["lineItems"]>[number],
): PresaleCartLine {
  const product = lineItem.product;
  return {
    key: product?.key ?? lineItem.productId,
    productId: lineItem.productId,
    // Seeded links store `""` for "use the product default" — normalized to null so the
    // price comparison below falls through to the amount instead of matching empty strings.
    priceId: lineItem.usedPriceId || null,
    name: product?.name ?? "Item",
    description: product?.description ?? "",
    quantity: lineItem.quantity,
    unitAmountCents: lineItem.usedPriceDetail?.unitAmount ?? null,
    interval: lineItem.usedPriceDetail?.interval ?? null,
    stripeProductId: product?.stripeProductId ?? null,
    isMembership: product?.type === MEMBERSHIP_PRODUCT_TYPE,
  };
}

/** Cents of service credit an offer includes — the one thing that varies between them. */
function creditCentsOf(lines: PresaleCartLine[]): number {
  const credit = lines.find((line) => line.key === SERVICE_CREDIT_KEY);
  return (credit?.unitAmountCents ?? 0) * (credit?.quantity ?? 1);
}

/**
 * The campaign's offers, cheapest credit first.
 *
 * Selection is by the market token in the key because a `CheckoutLink` carries no campaign
 * column — the token lives on the campaign (`PRESALE_CAMPAIGN.checkoutLinkKeyToken`) so the
 * next market ships as data, and every membership type sold into the market is included.
 * Inactive links are dropped here rather than at the call site: `add-my-place` rejects an
 * inactive link, so offering one is always a dead end.
 */
export function toPresaleOffers(
  links: readonly GetCheckoutLinksListItem[] | undefined,
  campaign: PresaleCampaign,
): PresaleOffer[] {
  return (links ?? [])
    .filter(
      (link) => link.active && isPresaleCheckoutLinkKey(link.key, campaign),
    )
    .map((link) => {
      const lines = [...(link.lineItems ?? [])]
        .sort((a, b) => a.order - b.order)
        .map(toCartLine);
      return {
        checkoutLinkId: link.id,
        key: link.key,
        name: link.name,
        description: link.description ?? "",
        lines,
        creditCents: creditCentsOf(lines),
      };
    })
    .sort((a, b) => a.creditCents - b.creditCents);
}

/**
 * The tier a checkout link sells, read off its `service-credit` line.
 *
 * `undefined` when the link sells no credit — the caller then has nothing to match on and
 * falls back, rather than matching every offer that happens to share a product.
 */
export function toPresaleOfferTarget(
  lineItems: readonly CheckoutLinkLine[] | null | undefined,
): PresaleOfferTarget | undefined {
  const credit = (lineItems ?? []).find(
    (item) => item.product?.key === SERVICE_CREDIT_KEY,
  );
  if (!credit) return undefined;
  return {
    productId: credit.productId,
    priceId: credit.usedPriceId || null,
    unitAmountCents: credit.usedPriceDetail?.unitAmount ?? null,
  };
}

/** Whether an offer sells the target's credit product at the target's exact price. */
function sellsExactTier(
  offer: PresaleOffer,
  target: PresaleOfferTarget,
): boolean {
  return offer.lines.some(
    (line) =>
      line.productId === target.productId &&
      (target.priceId
        ? line.priceId === target.priceId
        : line.unitAmountCents === target.unitAmountCents),
  );
}

/** Whether an offer sells the target's credit product at all, at any price. */
function sellsCreditProduct(
  offer: PresaleOffer,
  target: PresaleOfferTarget,
): boolean {
  return offer.lines.some((line) => line.productId === target.productId);
}

/**
 * The offer a funnel is selling.
 *
 * The link the buyer arrived on wins when it is one of the campaign's own rows: two rows can
 * sell the SAME credit tier, and the tier alone cannot tell them apart — it would silently
 * hand back whichever sorts first. Requiring the tier to agree as well keeps a stale id in
 * the URL from selling a cart it no longer matches.
 *
 * Falling through to the credit line rather than stopping there is what lets a link that is
 * not itself one of the campaign's rows still resolve to the tier it sells — such a link is
 * outside the key prefix, so it never appears in `offers` and can never match by id. The
 * price is part of that match because `service-credit` is ONE product across all four tiers
 * — they differ only by Stripe price, so `productId` alone matches every offer.
 *
 * Falling back rather than erroring is deliberate — a member who lands on `/presale` with
 * no link (a shared URL, a bookmark) still gets a working funnel instead of a dead end. It
 * is the only way such a visitor reaches a priced cart at all: checkout confirms the tier
 * read-only, so there is no picker to recover from an unresolved link.
 */
export function selectPresaleOffer(
  offers: readonly PresaleOffer[],
  target: PresaleOfferTarget | undefined,
  checkoutLinkId?: string,
): PresaleOffer | undefined {
  if (target) {
    const linked = offers.find(
      (offer) =>
        Boolean(checkoutLinkId) &&
        offer.checkoutLinkId === checkoutLinkId &&
        sellsExactTier(offer, target),
    );
    if (linked) return linked;

    const exact = offers.find((offer) => sellsExactTier(offer, target));
    if (exact) return exact;
    // The link's price is not one the campaign sells (a retired tier, a plane mismatch) —
    // the credit product still narrows it further than "the cheapest offer" would.
    const byProduct = offers.find((offer) => sellsCreditProduct(offer, target));
    if (byProduct) return byProduct;
  }

  // No tier to compare — the link's detail is still in flight, or it sells no credit line.
  // The id alone still beats rendering the cheapest tier and snapping to another one later.
  const byLink = checkoutLinkId
    ? offers.find((offer) => offer.checkoutLinkId === checkoutLinkId)
    : undefined;
  if (byLink) return byLink;

  return offers[0];
}
